import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { useTenantId } from '@/auth/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable, EmptyRow } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { prettyJson } from '@/lib/json';

/** /audit — append-only audit log viewer (10 §3), filtered by resource and actor. */
export function AuditPage() {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const [filters, setFilters] = useState({ resourceType: '', resourceId: '', actor: '' });
  const [draft, setDraft] = useState(filters);

  const query = useInfiniteQuery({
    queryKey: queryKeys.scoped(tenantId, 'audit', filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/v1/audit', {
          params: {
            query: {
              limit: 50,
              ...(pageParam ? { cursor: pageParam } : {}),
              ...(filters.resourceType ? { resourceType: filters.resourceType } : {}),
              ...(filters.resourceId ? { resourceId: filters.resourceId } : {}),
              ...(filters.actor ? { actor: filters.actor } : {}),
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div>
      <PageHeader help="audit" title={t('admin.audit.title')} description={t('admin.audit.description')} />
      <form
        className="flex flex-wrap items-end gap-2 border-b px-6 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters(draft);
        }}
      >
        <Input className="w-44" placeholder={t('admin.audit.resourceType')} aria-label={t('admin.audit.resourceType')} value={draft.resourceType} onChange={(e) => setDraft({ ...draft, resourceType: e.target.value })} />
        <Input className="w-72" placeholder={t('admin.audit.resourceId')} aria-label={t('admin.audit.resourceId')} value={draft.resourceId} onChange={(e) => setDraft({ ...draft, resourceId: e.target.value })} />
        <Input className="w-72" placeholder={t('admin.audit.actor')} aria-label={t('admin.audit.actor')} value={draft.actor} onChange={(e) => setDraft({ ...draft, actor: e.target.value })} />
        <Button type="submit" size="sm">
          {t('admin.audit.filter')}
        </Button>
      </form>
      <div className="p-6">
        <DataTable head={[t('admin.audit.at'), t('admin.audit.actor'), t('admin.audit.action'), t('admin.audit.resource'), t('admin.audit.change')]}>
          {rows.length === 0 && !query.isPending && <EmptyRow colSpan={5}>{t('admin.audit.empty')}</EmptyRow>}
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="whitespace-nowrap text-xs">{new Date(r.createdAt).toLocaleString()}</td>
              <td className="text-xs">
                <Badge>{r.actorType}</Badge> <span className="font-mono">{r.actorId ?? '—'}</span>
              </td>
              <td className="font-mono text-xs">{r.action}</td>
              <td className="text-xs">
                {r.resourceType} <span className="font-mono text-muted-foreground">{r.resourceId}</span>
              </td>
              <td>
                {(r.before !== null || r.after !== null) && (
                  <details>
                    <summary className="cursor-pointer text-xs text-muted-foreground">{t('admin.audit.show')}</summary>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <pre className="max-h-48 overflow-auto rounded bg-muted/40 p-2 font-mono text-[11px]">{prettyJson(r.before)}</pre>
                      <pre className="max-h-48 overflow-auto rounded bg-muted/40 p-2 font-mono text-[11px]">{prettyJson(r.after)}</pre>
                    </div>
                  </details>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
        {query.hasNextPage && (
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
              {t('admin.audit.more')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
